import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'
import { API_URLS } from '@chessx/shared/api-routes'
import type { OpeningPathPage, OpeningSearchResult } from '@chessx/shared/opening-tree'
import axiosInstance from '../../../network/axiosInstance'

interface OpeningSearchProps {
  onRevealLine: (san: readonly string[]) => void
  onJumpToPosition: (fen: string, line: readonly string[]) => void
}

const PAGE_SIZE = 5
const EMPTY_RESULT: OpeningSearchResult = { kind: 'name', position: null, lines: [] }

const NOT_IN_BOOK: Record<string, string> = {
  fen: 'This position is not in the Opening Tree',
  pgn: 'This line leaves the Opening Tree'
}

const formatLine = (san: readonly string[]): string =>
  san.map((move, index) => (index % 2 === 0 ? `${index / 2 + 1}. ${move}` : move)).join(' ')

const OpeningSearch = ({ onRevealLine, onJumpToPosition }: OpeningSearchProps) => {
  const [query, setQuery] = useState('')
  const [result, setResult] = useState<OpeningSearchResult>(EMPTY_RESULT)
  const [extraPaths, setExtraPaths] = useState<OpeningPathPage | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searching, setSearching] = useState(false)

  const runSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        if (value.trim() === '') {
          setResult(EMPTY_RESULT)
          setSearching(false)
          return
        }
        try {
          const response = await axiosInstance.get(API_URLS.OPENING_SEARCH, {
            params: { q: value }
          })
          setResult((response.data?.data ?? EMPTY_RESULT) as OpeningSearchResult)
        } catch {
          setResult(EMPTY_RESULT)
        } finally {
          setSearching(false)
        }
      }, 300),
    []
  )

  useEffect(() => () => runSearch.cancel(), [runSearch])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQuery(value)
    setExtraPaths(null)
    setSearching(value.trim() !== '')
    runSearch(value)
  }

  const dismiss = () => {
    setQuery('')
    setResult(EMPTY_RESULT)
    setExtraPaths(null)
  }

  const handleReveal = (san: readonly string[]) => {
    dismiss()
    onRevealLine(san)
  }

  const handleJump = (fen: string, line: readonly string[]) => {
    dismiss()
    onJumpToPosition(fen, line)
  }

  const { kind, position, lines } = result
  const paths = position === null ? null : (extraPaths ?? position.paths)
  const shownLines = paths?.lines ?? []
  const isPositionQuery = kind === 'fen' || kind === 'pgn'

  const loadMore = async () => {
    if (position === null || paths === null) return
    setLoadingMore(true)
    try {
      const response = await axiosInstance.get(API_URLS.OPENING_PATHS, {
        params: { fen: position.fen, offset: shownLines.length, limit: PAGE_SIZE }
      })
      const next = response.data?.data as OpeningPathPage
      setExtraPaths({
        lines: [...shownLines, ...next.lines],
        offset: 0,
        hasMore: next.hasMore
      })
    } catch {
      setExtraPaths({ lines: shownLines, offset: 0, hasMore: false })
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div className="otree-search">
      <div className="otree-search-field">
        <span className="otree-search-icon">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          type="text"
          value={query}
          placeholder="Search an opening name, a PGN (1. e4 e5 2. Nf3), or a FEN…"
          className="otree-search-input"
          onChange={handleChange}
        />
      </div>

      {query.trim() !== '' && (
        <div className="otree-search-results">
          {searching && <div className="otree-search-status">Searching…</div>}

          {!searching && isPositionQuery && position === null && (
            <div className="otree-search-status">{NOT_IN_BOOK[kind]}</div>
          )}

          {!searching && kind === 'name' && lines.length === 0 && (
            <div className="otree-search-status">No openings match “{query}”</div>
          )}

          {!searching && position !== null && (
            <div className="otree-position-card">
              <span className={`otree-position-name ${position.openingName ? '' : 'is-unnamed'}`}>
                {position.openingName ?? 'In the book, but unnamed'}
              </span>
              <span className="otree-position-meta">
                move {Math.ceil(position.ply / 2)} · {position.childCount} continuation
                {position.childCount === 1 ? '' : 's'} · {position.maxDepth} ply below
              </span>

              <button
                className="otree-position-jump"
                onClick={() => handleJump(position.fen, shownLines[0] ?? [])}
              >
                Open this position as the tree root
              </button>

              {shownLines.length > 0 && (
                <div className="otree-path-list">
                  <span className="otree-path-heading">
                    Ways to reach it {position.ply > 0 && `(${position.ply} plies)`}
                  </span>
                  {shownLines.map(line => (
                    <button
                      key={line.join(' ')}
                      className="otree-path-line"
                      onClick={() => handleReveal(line)}
                    >
                      {formatLine(line)}
                    </button>
                  ))}
                  {paths?.hasMore && (
                    <button className="otree-path-more" disabled={loadingMore} onClick={loadMore}>
                      {loadingMore ? 'Loading…' : `Show ${PAGE_SIZE} more`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {!searching && kind === 'pgn' && lines.length > 0 && (
            <span className="otree-path-heading otree-list-heading">
              Openings continuing from this line
            </span>
          )}

          {!searching &&
            lines.map(line => (
              <button
                key={line.nameIndex}
                className="otree-search-result"
                onClick={() => handleReveal(line.san)}
              >
                <span className="otree-search-result-name">{line.name}</span>
                <span className="otree-search-result-line">{formatLine(line.san)}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  )
}

export default OpeningSearch
